import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const [article, setArticle] = useState(articles.find((a) => a.id === id) || null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const found = articles.find((a) => a.id === id);
    if (found) {
      setArticle(found);
      setNotFound(false);
    } else {
      setNotFound(true);
    }
  }, [id]);

  if (notFound || !article) {
    return (
      <>
        <SEO title="文章未找到 - 蜜桃电影" description="您访问的文章不存在或已被移除" />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-16 flex items-center justify-center min-h-[60vh]">
            <div className="text-center px-4">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-200">
                <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
              </div>
              <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-2">文章未找到</h1>
              <p className="text-foreground-500 mb-6">您访问的文章不存在或已被移除</p>
              <Link
                to="/"
                className="px-6 py-2.5 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const catInfo = categories.find((c) => c.slug === article.category);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    description: article.summary,
    articleSection: catInfo?.name || '',
    keywords: article.tags.join(','),
    publisher: {
      '@type': 'Organization',
      name: '蜜桃电影',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://mitaomovie.com/' },
      { '@type': 'ListItem', position: 2, name: catInfo?.name || '分类', item: `https://mitaomovie.com/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  const schema = { ...articleSchema, ...breadcrumbSchema };

  const relatedArticles = articles
    .filter((a) => a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <SEO
        title={`${article.title} - 蜜桃电影`}
        description={article.summary}
        keywords={`蜜桃电影,${article.tags.join(',')},${catInfo?.name || ''}片,影评`}
        canonicalUrl={`https://mitaomovie.com/article/${article.id}`}
        ogType="article"
        ogImage={article.coverImage}
        articlePublishedTime={article.date}
        articleTags={article.tags}
        schema={schema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: catInfo?.name || '分类', to: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>

          {/* Article Header */}
          <section className="pb-8 md:pb-12">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="relative overflow-hidden rounded-lg aspect-[2/1] bg-background-200 mb-8">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  title={article.title}
                  className="w-full h-full object-cover"
                />
              </div>

              {catInfo && (
                <span className="inline-block bg-primary-500 text-background-50 dark:text-foreground-950 text-xs font-medium px-3 py-1 rounded-full mb-4">
                  {catInfo.name}
                </span>
              )}

              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-3 text-sm text-foreground-500 mb-6 flex-wrap">
                <span>{article.author}</span>
                <span className="text-foreground-300">|</span>
                <span>{article.date}</span>
                <span className="text-foreground-300">|</span>
                <span>阅读约 {article.readTime}</span>
              </div>

              <p className="text-base text-foreground-600 bg-background-100 rounded-lg p-5 leading-relaxed border-l-4 border-primary-400">
                {article.summary}
              </p>

              {article.tags.length > 0 && (
                <div className="flex items-center gap-2 mt-5 flex-wrap">
                  {article.tags.map((tag) => (
                    <span key={tag} className="text-xs bg-secondary-100 text-secondary-600 px-2.5 py-1 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Article Content */}
          <section className="py-8 md:py-12 bg-background-50">
            <article className="max-w-3xl mx-auto px-4 md:px-6">
              <div
                className="prose-custom text-foreground-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </article>
          </section>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="py-14 md:py-20 bg-background-100">
              <div className="max-w-7xl mx-auto px-4 md:px-6">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-8">
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                  {relatedArticles.map((ra) => (
                    <ArticleCard
                      key={ra.id}
                      id={ra.id}
                      title={ra.title}
                      category={ra.category}
                      author={ra.author}
                      date={ra.date}
                      summary={ra.summary}
                      coverImage={ra.coverImage}
                      tags={ra.tags}
                      readTime={ra.readTime}
                    />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>

      <style>{`
        .prose-custom h2 {
          font-family: var(--font-heading);
          font-size: 1.5rem;
          font-weight: 700;
          color: oklch(var(--foreground-900));
          margin-top: 2rem;
          margin-bottom: 1rem;
        }
        .prose-custom p {
          font-size: 1rem;
          line-height: 1.9;
          color: oklch(var(--foreground-700));
          margin-bottom: 1.25rem;
        }
      `}</style>
    </>
  );
}